/**
 * Санитайзер контекста событий Hawk — один на sso-web, id-web, admin и server.
 *
 * Трекер ошибок внешний, поэтому фильтр здесь строже, чем `redact` в Pino:
 * там маскировка нужна для читаемости своих же логов, здесь — чтобы наружу
 * физически не уехало ничего приватного. Режем по подстроке в имени ключа
 * и не пытаемся угадывать точные пути: список ключей растёт вместе с кодовой
 * базой, а список путей неизбежно отстаёт.
 */

/** Глубина обхода объекта контекста. */
const MAX_DEPTH = 4;

/** Максимальная длина строкового значения. */
const MAX_STRING_LENGTH = 512;

/** Максимум элементов массива. */
const MAX_ARRAY_ITEMS = 10;

/** Подстроки в имени ключа, значение которого не покидает приложение. */
const SENSITIVE_KEY_PATTERNS = [
	"password",
	"token",
	"secret",
	"apikey",
	"api_key",
	"authorization",
	"cookie",
	"session",
	"otp",
	"magiclink",
	"verifyurl",
	"email",
	"phone",
	"content",
] as const;

/** Query-параметры, вырезаемые из URL перед отправкой. */
const SENSITIVE_QUERY_PARAMS = [
	"token",
	"code",
	"secret",
	"key",
	"apikey",
	"access_token",
	"id_token",
	"refresh_token",
	"state",
	"email",
] as const;

function isSensitiveKey(key: string): boolean {
	const normalized = key.toLowerCase();
	return SENSITIVE_KEY_PATTERNS.some((pattern) => normalized.includes(pattern));
}

/**
 * Рекурсивно приводит значение к безопасному для отправки виду: вырезает
 * чувствительные ключи, обрезает строки, ограничивает глубину и размер.
 */
export function scrubForHawk(value: unknown, depth = 0): unknown {
	if (value === null || value === undefined) return value;

	if (typeof value === "string") {
		return value.length > MAX_STRING_LENGTH ? `${value.slice(0, MAX_STRING_LENGTH)}…` : value;
	}

	if (typeof value === "number" || typeof value === "boolean") return value;
	if (typeof value === "bigint") return value.toString();
	if (value instanceof Date) return value.toISOString();
	if (typeof value === "function" || typeof value === "symbol") return undefined;

	if (depth >= MAX_DEPTH) return "[Depth limit]";

	if (Array.isArray(value)) {
		const items = value.slice(0, MAX_ARRAY_ITEMS).map((item) => scrubForHawk(item, depth + 1));
		return value.length > MAX_ARRAY_ITEMS
			? [...items, `…ещё ${value.length - MAX_ARRAY_ITEMS}`]
			: items;
	}

	if (value instanceof Error) {
		return {
			name: value.name,
			message: scrubForHawk(value.message, depth + 1),
		};
	}

	if (typeof value === "object") {
		const result: Record<string, unknown> = {};
		for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
			if (isSensitiveKey(key)) {
				result[key] = "[REDACTED]";
				continue;
			}
			const scrubbed = scrubForHawk(item, depth + 1);
			if (scrubbed !== undefined) result[key] = scrubbed;
		}
		return result;
	}

	return undefined;
}

/**
 * Чистит URL перед отправкой: убирает одноразовые коды и токены из query.
 *
 * Актуально для breadcrumb'ов fetch/навигации: ссылка входа из письма
 * (`/signin?code=…`) и OAuth-callback'и иначе уехали бы во внешний сервис
 * целиком, а это полный доступ к аккаунту, пока не истёк TTL.
 */
export function scrubUrlForHawk(rawUrl: string): string {
	try {
		const url = new URL(rawUrl, "http://local");
		let touched = false;
		for (const param of SENSITIVE_QUERY_PARAMS) {
			if (url.searchParams.has(param)) {
				url.searchParams.set(param, "[REDACTED]");
				touched = true;
			}
		}
		if (!touched) return rawUrl;
		return rawUrl.startsWith("http") ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
	} catch {
		return rawUrl;
	}
}
